import { Skeleton, Typography } from 'antd';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Calendar } from 'api/timesheets/integration/calendar';
import { TitleSelect } from 'components/organisms/timesheets/common/title-select';
import { useTimesheetCalendarQuery } from 'queries/timesheets/calendar-integration-actions';
import { Content, Empty, Loaded } from 'stores/core';
import { LinkTrigger } from '../../components';
import { SelectCalenderWrapper } from '../component';

interface SelectCalenderProps {
  onChange: (val: Content<Calendar>) => void;
  selection: Content<Calendar>;
  integrationId: number;
}

const optionsTransformer = (cal: Calendar) => ({
  label: cal.calendarName,
  value: cal.calendarId,
});

export const SelectCalender: FC<SelectCalenderProps> = ({
  onChange: _onChange,
  selection,
  integrationId,
}) => {
  const [_, selectedCalendar] = selection;
  const selectedId = selectedCalendar?.calendarId;
  const { data, isLoading } = useTimesheetCalendarQuery(integrationId);

  useEffect(() => {
    // auto select primary calender once calender loads
    if (selectedId || isLoading) {
      return;
    }
    if (!data) {
      _onChange(Empty());

      return;
    }
    const primary =
      data.calendars.find((calender) => calender.primary) || data.calendars[0];

    _onChange(Loaded(primary));
  }, [_onChange, data, isLoading, selectedId]);

  const options = useMemo(
    () => data?.calendars?.map(optionsTransformer),
    [data],
  );
  const selected = useMemo(
    () => options?.find((option) => option.value === selectedId),
    [options, selectedId],
  );
  const onChange = useCallback(
    ({ value }) => {
      const pickedCalendar = data?.calendars.find(
        (calender) => calender.calendarId === value,
      );

      if (!pickedCalendar) {
        return;
      }

      _onChange(Loaded(pickedCalendar));
    },
    [_onChange, data?.calendars],
  );

  if (isLoading) {
    return <Skeleton paragraph={{ rows: 1 }} />;
  }
  if (!options || !selected) {
    return null;
  }

  return (
    <SelectCalenderWrapper>
      <Typography.Text>
        <FormattedMessage id="timesheets.integrations.calender.select" />
      </Typography.Text>
      <TitleSelect
        options={options}
        selected={selected}
        onChange={onChange}
        triggerComponent={LinkTrigger}
      />
    </SelectCalenderWrapper>
  );
};
